import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Page,
  Layout,
  Card,
  EmptyState,
  IndexTable,
  Text,
  Badge,
  Tabs,
} from "@shopify/polaris";
import { usePurchaseOrders, type PurchaseOrder } from "../hooks/usePurchaseOrders";
import { formatCurrency } from "../utils/cost-calculations";

const STATUS_TONES: Record<string, "info" | "attention" | "success" | "critical" | undefined> = {
  draft: "info",
  submitted: "attention",
  partially_received: "attention",
  fully_received: "success",
  cancelled: "critical",
  closed: undefined,
};

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  submitted: "Submitted",
  partially_received: "Partially Received",
  fully_received: "Fully Received",
  cancelled: "Cancelled",
  closed: "Closed",
};

const TABS = [
  { id: "all", content: "All", status: undefined },
  { id: "draft", content: "Draft", status: "draft" },
  { id: "submitted", content: "Submitted", status: "submitted" },
  { id: "partially_received", content: "Partially Received", status: "partially_received" },
  { id: "fully_received", content: "Fully Received", status: "fully_received" },
];

export default function POList() {
  const navigate = useNavigate();
  const { purchaseOrders, total, loading, loadPurchaseOrders } = usePurchaseOrders();
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    const tab = TABS[selectedTab];
    loadPurchaseOrders(tab.status ? { status: tab.status } : undefined);
  }, [selectedTab]);

  const handleTabChange = useCallback(
    (index: number) => setSelectedTab(index),
    []
  );

  const resourceName = {
    singular: "purchase order",
    plural: "purchase orders",
  };

  const emptyStateMarkup = (
    <EmptyState
      heading="No purchase orders yet"
      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
    >
      <p>
        Select a vendor from the main page to start building a purchase order.
      </p>
    </EmptyState>
  );

  const rowMarkup = purchaseOrders.map((po, index) => (
    <IndexTable.Row
      id={po.id}
      key={po.id}
      selected={false}
      position={index}
      onClick={() => navigate(`/purchase-orders/${po.id}`)}
    >
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="bold" as="span">
          {po.poNumber}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{po.vendorName || "\u2014"}</IndexTable.Cell>
      <IndexTable.Cell>
        {po.orderDate ? new Date(po.orderDate).toLocaleDateString() : "\u2014"}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={STATUS_TONES[po.status]}>
          {STATUS_LABELS[po.status] || po.status}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>{formatCurrency(po.total)}</IndexTable.Cell>
      <IndexTable.Cell>{String(po.lineItemCount ?? 0)}</IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page
      title="All Purchase Orders"
      backAction={{ content: "Vendors", url: "/" }}
    >
      <Layout>
        <Layout.Section>
          <Card padding="0">
            <Tabs
              tabs={TABS.map((t) => ({ id: t.id, content: t.content, panelID: t.id }))}
              selected={selectedTab}
              onSelect={handleTabChange}
            >
              {purchaseOrders.length === 0 && !loading ? (
                <div style={{ padding: "16px" }}>{emptyStateMarkup}</div>
              ) : (
                <IndexTable
                  resourceName={resourceName}
                  itemCount={purchaseOrders.length}
                  selectedItemsCount={0}
                  onSelectionChange={() => {}}
                  headings={[
                    { title: "PO Number" },
                    { title: "Vendor" },
                    { title: "Date" },
                    { title: "Status" },
                    { title: "Total" },
                    { title: "Items" },
                  ]}
                  loading={loading}
                  selectable={false}
                >
                  {rowMarkup}
                </IndexTable>
              )}
            </Tabs>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
